import React, {
  useLayoutEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import { Stack, Typography } from "@mui/material";
import s from "./OfflineMerchantDetail.module.scss";
import EditIcon from "@mui/icons-material/Edit";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import {
  queryMerchantDetail,
  queryMerchantStatusOptions,
  queryDropdownOptions,
  queryMerchantOtherDetail,
  updateMerchant,
} from "./model";
import responseCodeMapping from "utils/response_code_mapping.json";

// --- Different tabs' components --- //
import MerchantInfo from "./merchant-info/MerchantInfo";
import MerchantEmployee from "./merchant-employee/MerchantEmployee";
import MerchantConfig from "./merchant-config/MerchantConfig";
import MerchantProcessorMDR from "./merchant-processor-mdr/MerchantProcessorMDR";
import MerchantMaintenanceRecord from "./merchant-maintenance-record/MerchantMaintenanceRecord";
import MerchantSMEUsage from "./merchant-sme-usage/MerchantSMEUsage";
import MerchantManagementOfflineMerchantProcessorAccounts from "../offline-merchant-processor-accounts/OfflineMerchantProcessorAccounts";
// --- Different tabs' components --- //

import SimpleDialog from "components/SimpleDialog/SimpleDialog";
import AccountStatusDialog from "../../components/account-status/AccountStatusDialog";
import moment from "moment";
import { isNullish } from "utils/utils";
import { useSnackbar } from "notistack";
import ButtonMAS from "ButtonMAS/ButtonMAS";
import usePageWrapper from "utils/usePageWrapper/usePageWrapper";
import usePageState from "utils/usePageState";
import Notice from "components/Notice/Notice";

const DIALOG_LOCALE_KEY = "page.dialogs.";
const MERCHANT_LOCALE_TABS_KEY = "page.merchants.tabs";
const MERCHANT_LOCALE_KEY = "page.merchants.merchantDetail.";

const TAB_LIST_STYLE = {
  "& .MuiTabs-indicator": {
    backgroundColor: "#309ea7",
  },
  "& .MuiTab-root": {
    whiteSpace: "nowrap",
  },
};
const TAB_STYLE = {
  "&.Mui-selected": {
    color: "#309ea7",
  },
  textTransform: "none",
};
const TAB_PANEL_STYLE = { padding: 0 };

const PARTITION_KEYS = /** @type {const} */ ([
  "info",
  "employee",
  "config",
  "processorMdr",
  "processorAccounts",
  "maintenanceRecord",
  "smeUsage",
  "merchantStatus",
]);
/** @typedef {typeof PARTITION_KEYS[number]} offlineMerchantDetailPartitionKeyType */

const OfflineMerchantDetail = ({ path, redirectFn }) => {
  const { t } = useTranslation();
  const { pageState } = usePageState({ pagePath: path });
  const { merchantId: stateMerchantId } = pageState;
  const { enqueueSnackbar } = useSnackbar();
  const { callApis, PageWrapper, PageOuterContainer, PageStickyHeader, PageBody } = usePageWrapper({
    partitionKeys: PARTITION_KEYS,
  });

  const [tabValue, setTabValue] = useState("info");
  const [merchantId, setMerchantId] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [
    merchantExternalStatusSubmitting,
    setMerchantExternalStatusSubmitting,
  ] = useState(false);
  const [merchant, setMerchant] = useState({});
  const [isAccountStatusDialogOpen, setIsAccountStatusDialogOpen] =
    useState(false);
  const [
    isExternalMerchantStatusDialogOpen,
    setIsExternalMerchantStatusDialogOpen,
  ] = useState(false);
  const externalMerchantStatusPrcessor = useRef(null);
  const [statusRemarks, setStatusRemarks] = useState();
  const [serviceIntegratorOptions, setServiceIntegratorOptions] = useState();
  const [merchantOtherDetail, setMerchantOtherDetail] = useState({});
  const [dropdownOptions, setDropdownOptions] = useState();
  const AGENT_STATUS_INIT_STATE = {
    status: "",
    lockAccountType: "",
    remark: "",
  };
  const [accountStatusDialogData, setAccountStatusDialogData] = useState(
    AGENT_STATUS_INIT_STATE,
  );

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  const isStatusRemarksAvailable = useMemo(() => statusRemarks?.["TERMINATED"] && statusRemarks?.["SUSPENDED"], [statusRemarks]);

  const staticMerchantPayload = useCallback(
    (merchant, tab, specialCheckFields) => {
      const payload = {
        merchantId: merchant?.merchantId,
        logoPath: merchant?.logoPath,
        ciNumber: merchant?.ciNumber,
        phone: merchant?.phone,
        businessEstablishedDate: moment(merchant?.businessEstablishedDate),
        serviceIntegrator: merchant?.serviceIntegrator,
        summaryReportTime: moment(merchant?.summaryReportTime),
        otherInfo: merchant?.otherInfo,
        externalMerchantStatus: merchant?.externalMerchantStatus,
      };
      if (
        !tab ||
        (tab === "config" && specialCheckFields?.previewUrl) ||
        tab === "info"
      ) {
        payload.largeImagePath = merchant?.largeImagePath;
      }
      return payload;
    },
    [merchant],
  );

  // -------- Handle Status dialog -------- //
  const openAccountStatusDialog = (data) => {
    setAccountStatusDialogData(data);
    setIsAccountStatusDialogOpen(true);
  };

  const closeAccountStatusDialog = () => {
    restoreAccountStatusDialog();
    setIsAccountStatusDialogOpen(false);
  };

  const restoreAccountStatusDialog = () => {
    setAccountStatusDialogData(AGENT_STATUS_INIT_STATE);
  };

  const updatePayload = (fieldInput) => {
    setAccountStatusDialogData(fieldInput);
  };

  const AccountStatusDialogContent = () => {
    return (
      <AccountStatusDialog
        data={accountStatusDialogData}
        remarksList={statusRemarks}
        updatePayload={updatePayload}
      />
    );
  };

  const accountStatusDialogValidationCheck = () => {
    if (accountStatusDialogData.status !== "ACTIVE") {
      const validationResult =
        !!accountStatusDialogData.reason &&
        !!accountStatusDialogData.reason !== "";
      if (!validationResult) {
        snackbar(
          t(DIALOG_LOCALE_KEY + "changeStatus.msgError") +
          " Reason must not be empty!",
          "error",
        );
      }
      return validationResult;
    }
    return true;
  };

  const updateResultCallback = async () => {
    if (accountStatusDialogValidationCheck()) {
      setSubmitting(true);
      const reasonList = statusRemarks[accountStatusDialogData.status];
      const remarkOthersKey = "OTHERS";

      let remarksend =
        reasonList && (reasonList[accountStatusDialogData.reason] ?? "");
      if (accountStatusDialogData.reason == remarkOthersKey)
        remarksend = accountStatusDialogData.remark;

      let payload = {
        ...staticMerchantPayload(merchant),
        merchantId,
        isStatusChange: accountStatusDialogData.status != merchant.status,
        status: accountStatusDialogData.status,
      };

      if (!isNullish(accountStatusDialogData.reason))
        payload = {
          ...payload,
          lockAccountType: accountStatusDialogData.reason,
        };
      if (!isNullish(remarksend)) payload = { ...payload, remark: remarksend };

      const [updateMerchantResp] = await callApis([{
        api: updateMerchant,
        payload: payload,
        blockOnError: false,
        blockOnUnauthorized: false,
      }])
      if (updateMerchantResp.isSuccess) {
        snackbar(
          t(
            DIALOG_LOCALE_KEY +
            "changeStatus.msgSuccess." +
            accountStatusDialogData.status.toLowerCase(),
          ),
          "success",
        );
      } else {
        updateMerchantResp.isError && snackbar(
          t(DIALOG_LOCALE_KEY + "changeStatus.msgFailed") +
          responseCodeMapping[updateMerchantResp.responseCode ?? "error"][
          "en"
          ],
          "error",
        );
      }
      setSubmitting(false);
      closeAccountStatusDialog();
      await fetchMerchantDetail();
    }
  };

  const handleDeleteExternalMerchantStatus = async (processorBeingDeleted) => {
    setMerchantExternalStatusSubmitting(true);
    console.log("processorBeingDeleted", processorBeingDeleted);
    console.log("merchant", merchant);
    const externalMerchantStatus = Object.keys(
      merchant?.externalMerchantStatus,
    ).reduce((acc, key) => {
      if (key !== processorBeingDeleted) {
        acc[key] = merchant?.externalMerchantStatus[key];
      }
      return acc;
    }, {});
    try {
      const payload = {
        ...staticMerchantPayload(merchant),
        merchantId,
        externalMerchantStatus: externalMerchantStatus,
      };
      const [updateMerchantResp] = await callApis([{
        api: updateMerchant,
        payload: payload,
        blockOnError: false,
        blockOnUnauthorized: false,
      }])
      if (updateMerchantResp.isSuccess) {
        snackbar(
          t(
            MERCHANT_LOCALE_KEY +
            "fetchMessage.updateSuccess",
          ),
          "success",
        );
      } else if (updateMerchantResp.isError) {
        snackbar(
          t(
            MERCHANT_LOCALE_KEY +
            "fetchMessage.updateFailed",
          ),
          "error",
        );
      }
    } catch (error) {
      console.error("Error fetching merchant detail:", error);
    } finally {
      setMerchantExternalStatusSubmitting(false);
      setIsExternalMerchantStatusDialogOpen(false);
      fetchMerchantDetail();
    }
  };

  // -------- Handle Status dialog -------- //

  useLayoutEffect(() => {
    if (stateMerchantId) setMerchantId(stateMerchantId);
    else redirectFn("close", { tabId: path });
  }, [stateMerchantId]);

  const fetchMerchantDetail = async () => {
    console.log("fetchMerchantDetail merchantId: ", merchantId);
    try {
      setLoading(true);
      const [merchantDetailResp] = await callApis([{
        api: queryMerchantDetail,
        payload: merchantId,
        partitionKeys: ["info", "config", "processorMdr"],
      }])
      if (!merchantDetailResp.isSuccess) {
        merchantDetailResp.isError && snackbar(
          t(MERCHANT_LOCALE_KEY + "fetchMessage.queryDetailFailed"),
          "error",
        );
      } else {
        setMerchant(merchantDetailResp?.data);
      }
    } catch (error) {
      console.error("Error fetching merchant detail:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMerchantAndMerchantOtherDetail = async () => {
    try {
      const [merchantDetailResp, merchantOtherDetailResp] = await callApis([
        {
          api: queryMerchantDetail,
          payload: merchantId,
          partitionKeys: ["info", "config", "processorMdr"],
        },
        {
          api: queryMerchantOtherDetail,
          payload: { merchantId },
          partitionKeys: ["info", "config"],
        }
      ]);
      if (!merchantDetailResp.isSuccess) {
        merchantDetailResp.isError && snackbar(
          t(MERCHANT_LOCALE_KEY + "fetchMessage.queryDetailFailed"),
          "error",
        )
      } else {
        setMerchant(merchantDetailResp?.data);
      }
      if (!merchantOtherDetailResp.isSuccess) {
        merchantOtherDetailResp.isError && snackbar(
          t(MERCHANT_LOCALE_KEY + "fetchMessage.queryOtherDetailFailed"),
          "error",
        )
      } else {
        setMerchantOtherDetail(merchantOtherDetailResp?.data);
      }
    } catch (error) {
      console.error("Error fetching merchant detail:", error);
    } finally {
      setLoading(false);
    }
  };

  const initialFetchDetails = async () => {
    try {
      setLoading(true);
      const [merchantDetailResp, merchantStatusOptionsResp, dropdownOptionsResp, merchantOtherDetailResp] = await callApis([
        {
          api: queryMerchantDetail,
          payload: merchantId,
          partitionKeys: ["info", "config", "processorMdr"],
        },
        {
          api: queryMerchantStatusOptions,
          partitionKeys: ["merchantStatus"],
        },
        {
          api: queryDropdownOptions,
          partitionKeys: ["info", "processorMdr"],
        },
        {
          api: queryMerchantOtherDetail,
          payload: { merchantId },
          partitionKeys: ["info", "config"],
        }
      ])

      if (!merchantDetailResp.isSuccess) {
        merchantDetailResp.isError && snackbar(
          t(MERCHANT_LOCALE_KEY + "fetchMessage.queryDetailFailed"),
          "error",
        )
      } else {
        setMerchant(merchantDetailResp?.data);
      }

      if (!merchantStatusOptionsResp.isSuccess) {
        merchantStatusOptionsResp.isError && snackbar(
          t(MERCHANT_LOCALE_KEY + "fetchMessage.queryStatusOptionsFailed"),
          "error",
        )
      } else {
        setStatusRemarks(merchantStatusOptionsResp?.data);
      }

      if (!dropdownOptionsResp.isSuccess) {
        dropdownOptionsResp.isError && snackbar(
          t(MERCHANT_LOCALE_KEY + "fetchMessage.queryDropdownOptionsFailed"),
          "error",
        )
      } else {
        setDropdownOptions(dropdownOptionsResp.data);
        setServiceIntegratorOptions(dropdownOptionsResp?.data?.serviceIntegratorList);
      }

      if (!merchantOtherDetailResp.isSuccess) {
        merchantOtherDetailResp.isError && snackbar(
          t(MERCHANT_LOCALE_KEY + "fetchMessage.queryOtherDetailFailed"),
          "error",
        )
      } else {
        setMerchantOtherDetail(merchantOtherDetailResp?.data);
      }
    } catch (error) {
      console.error("Error fetching merchant detail:", error);
    } finally {
      setLoading(false);
    }
  };

  useLayoutEffect(() => {
    if (merchantId) {
      initialFetchDetails();
    }
  }, [merchantId]);

  const tabs = () => (
    <>
      <PageWrapper noPadding partitionKey={tabValue} isLoading={loading}>
        <PageBody>
          <TabPanel value="info" style={TAB_PANEL_STYLE}>
            <MerchantInfo
              callApis={callApis}
              merchant={merchant}
              serviceIntegratorOptions={serviceIntegratorOptions}
              setMerchant={setMerchant}
              setMerchantOtherDetail={setMerchantOtherDetail}
              merchantOtherDetail={merchantOtherDetail}
              dropdownOptions={dropdownOptions}
              staticMerchantPayload={staticMerchantPayload}
              fetchMerchantAndMerchantOtherDetail={
                fetchMerchantAndMerchantOtherDetail
              }
            />
          </TabPanel>
          <TabPanel value="employee" style={TAB_PANEL_STYLE}>
            <MerchantEmployee
              callApis={callApis}
              merchantId={merchantId}
              redirectFn={redirectFn}
            />
          </TabPanel>
          <TabPanel value="config" style={TAB_PANEL_STYLE}>
            <MerchantConfig
              callApis={callApis}
              merchant={merchant}
              merchantOtherDetail={merchantOtherDetail}
              setMerchant={setMerchant}
              setMerchantOtherDetail={setMerchantOtherDetail}
              fetchMerchantAndMerchantOtherDetail={
                fetchMerchantAndMerchantOtherDetail
              }
              staticMerchantPayload={staticMerchantPayload}
            />
          </TabPanel>
          <TabPanel value="processorMdr" style={TAB_PANEL_STYLE}>
            <MerchantProcessorMDR
              callApis={callApis}
              merchantId={merchantId}
              fetchMerchantDetail={fetchMerchantDetail}
              mccOptions={dropdownOptions?.mccList}
              merchant={merchant}
              path={path}
            />
          </TabPanel>
          <TabPanel value="processorAccounts" style={TAB_PANEL_STYLE}>
            <MerchantManagementOfflineMerchantProcessorAccounts
              callApis={callApis}
              merchantId={merchantId}
              fetchMerchantDetail={fetchMerchantDetail}
              path={path}
            />
          </TabPanel>
          <TabPanel value="maintenanceRecord" style={TAB_PANEL_STYLE}>
            <MerchantMaintenanceRecord
              callApis={callApis}
              merchantId={merchantId}
              serviceIntegratorOptions={serviceIntegratorOptions}
            />
          </TabPanel>
          <TabPanel value="smeUsage" style={TAB_PANEL_STYLE}>
            <MerchantSMEUsage callApis={callApis} merchantId={merchantId} />
          </TabPanel>
        </PageBody>
      </PageWrapper>
    </>
  );

  const warning = (merchant) => {
    const { status, remark, terminatedDate, externalMerchantStatus } =
      merchant;

    const alertList = [];

    // Account status warning
    if (["TERMINATED", "SUSPENDED"].includes(status)) {
      alertList.push(
        <Notice
          severity={status === "TERMINATED" ? "error" : "warning"}
        >
          {`Account is ${status.toLowerCase()}. Reason: ${remark} (${moment(terminatedDate).format("YYYY-MM-DD HH:mm:ss")})`}
        </Notice>,
      );
    }

    // Processor failures warning
    externalMerchantStatus &&
      Object.entries(externalMerchantStatus).forEach(([processor, value]) => {
        alertList.push(
          <Notice
            severity="error"
            onClose={processor && (() => {
              setIsExternalMerchantStatusDialogOpen(true);
              externalMerchantStatusPrcessor.current = processor;
            })}
          >
            {`${processor} ` + value?.message || "account creation failure."}
          </Notice>,
        );
      });

    if (alertList.length) return <>{alertList}</>;
    else return null;
  };

  const header = () => (
    <>
      <Typography variant="h2" sx={{ mb: 1 }}>
        {t(`${MERCHANT_LOCALE_KEY}header.merchantName`)}:{" "}
        {merchant?.name}
      </Typography>
      <Stack
        direction={"row"}
        justifyContent="space-between"
        alignItems={"center"}
      >
        <Typography>
          {t(`${MERCHANT_LOCALE_KEY}header.merchantId`)}:{" "}
          {merchantId}
        </Typography>
        <Stack direction={"row"} gap={1} alignItems={"center"}>
          {t(`${MERCHANT_LOCALE_KEY}header.status`)}
          <Stack
            className={[
              s.statusContainer,
              merchant?.status === "SUSPENDED"
                ? s.statusSuspended
                : merchant?.status === "TERMINATED"
                  ? s.statusTerminated
                  : s.statusActive,
            ]}
          >
            {merchant?.status}
          </Stack>
          <ButtonMAS
            variant="text-only"
            className={s.inlineIconButton}
            style={{ display: merchant?.status === "TERMINATED" ? "none" : "" }}
            onClick={() => {
              openAccountStatusDialog(merchant);
            }}
            disabled={loading || !isStatusRemarksAvailable}
          >
            <EditIcon sx={{ color: "#309ea7" }} />
          </ButtonMAS>
        </Stack>
      </Stack>
      <>{warning(merchant)}</>

      <TabList
        onChange={(event, newValue) => setTabValue(newValue)}
        sx={TAB_LIST_STYLE}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab
          label={t(`${MERCHANT_LOCALE_TABS_KEY}.info`)}
          value="info"
          sx={TAB_STYLE}
        />
        <Tab
          label={t(`${MERCHANT_LOCALE_TABS_KEY}.employee`)}
          value="employee"
          sx={TAB_STYLE}
        />
        <Tab
          label={t(`${MERCHANT_LOCALE_TABS_KEY}.config`)}
          value="config"
          sx={TAB_STYLE}
        />
        <Tab
          label={t(`${MERCHANT_LOCALE_TABS_KEY}.processorMdr`)}
          value="processorMdr"
          sx={TAB_STYLE}
        />
        <Tab
          label={t(`${MERCHANT_LOCALE_TABS_KEY}.processorAccounts`)}
          value="processorAccounts"
          sx={TAB_STYLE}
        />
        <Tab
          label={t(`${MERCHANT_LOCALE_TABS_KEY}.maintenanceRecord`)}
          value="maintenanceRecord"
          sx={TAB_STYLE}
        />
        <Tab
          label={t(`${MERCHANT_LOCALE_TABS_KEY}.smeUsage`)}
          value="smeUsage"
          sx={TAB_STYLE}
        />
      </TabList>
    </>
  );

  return (
    <PageOuterContainer>
      <TabContext value={tabValue}>
        <PageStickyHeader noBottomPadding>
          {header()}
        </PageStickyHeader>
        {tabs()}

        {/* The merchant status handler */}
        <SimpleDialog
          open={isAccountStatusDialogOpen}
          dialogOncloseFn={closeAccountStatusDialog}
          title={t(DIALOG_LOCALE_KEY + "changeStatus.title")}
          content={AccountStatusDialogContent()}
          positiveAction={t(DIALOG_LOCALE_KEY + "btnSubmit")}
          negativeAction={t(DIALOG_LOCALE_KEY + "btnCancel")}
          positiveActionLoading={submitting}
          negativeActionDisabled={submitting}
          confirmFn={
            !submitting
              ? updateResultCallback
              : () => {
                console.log("submitting?", submitting);
              }
          }
        />

        <SimpleDialog
          open={isExternalMerchantStatusDialogOpen}
          dialogOncloseFn={() => {
            setIsExternalMerchantStatusDialogOpen(false);
          }}
          title={t(
            `${MERCHANT_LOCALE_KEY}header.errorDialog.title`,
          )}
          content={`Confirm ${externalMerchantStatusPrcessor.current} account setup success?`}
          positiveAction={t(DIALOG_LOCALE_KEY + "btnSubmit")}
          negativeAction={t(DIALOG_LOCALE_KEY + "btnCancel")}
          positiveActionLoading={merchantExternalStatusSubmitting}
          negativeActionDisabled={merchantExternalStatusSubmitting}
          confirmFn={() => {
            handleDeleteExternalMerchantStatus(
              externalMerchantStatusPrcessor.current,
            );
          }}
        />
      </TabContext>
    </PageOuterContainer>
  );
};

export default React.memo(OfflineMerchantDetail);
